<template>
	<div class="animation-container" :class="{'no-pointer': currAnimationPax || currAnimationDojo}" @click="toggleAnimationSelector">
		<div :id="'pig-pax-payment-'+uniqueId" class="pig-animation" v-show="currAnimationPax"></div>
		<div :id="'pig-dojo-payment-'+uniqueId" class="pig-animation" v-show="currAnimationDojo"></div>
		<div :id="'pig-payment-'+uniqueId" class="pig-animation" v-show="!currAnimationPax && !currAnimationDojo"></div>

		<!-- Animation selector -->
		<div class="animation-selector" v-if="showAnimationSelector && !currAnimationPax && !currAnimationDojo" @click.stop>
			<div
				v-for="animation in animations"
				:key="animation.type"
				class="animation-icon"
				:class="{ 'active': selectedAnimation === animation.type }"
				@click="selectAnimation(animation.type)"
				v-html="svgStore[animation.icon]"
			></div>
		</div>
	</div>
</template>

<script>
import { useSvgStore } from '@/shared/stores/svg.store.js'
export default {
	name: "PhysicalPosAnimation",
	setup() {
		const svgStore = useSvgStore();
		return { svgStore };
	},
	data() {
		return {
			uniqueId: "",
			loadedPig: false,
			loadedPigPax: false,
			loadedPigDojo: false,
			loadedElephant: false,
			loadedChameleon: false,
			pigAnimation: null,
			pigAnimationPax: null,
			pigAnimationDojo: null,
			elephantAnimation: null,
			chameleonAnimation: null,
			currAnimationPax: false,
			currAnimationDojo: false,
			lastCommand: null,
			lastTerminalId: null,
			showAnimationSelector: false,
			selectedAnimation: this.getStoredAnimation(),
			animations: [
				{
					type: 'pig',
					icon: 'pigIconAnimation',
					url: 'https://img.qromo.io/img/vue/anim/pig.json',
					urlPax: 'https://img.qromo.io/img/vue/anim/pig-pax.json',
					urlDojo: 'https://img.qromo.io/img/vue/anim/pig-dojo.json'
				},
				{
					type: 'elephant',
					icon: 'elephantIconAnimation',
					url: 'https://img.qromo.io/img/vue/anim/elephant.json',
				},
				{
					type: 'chameleon',
					icon: 'chameleonIconAnimation',
					url: 'https://img.qromo.io/img/vue/anim/chameleon.json',
				}
			]
		}
	},
	methods: {
		getStoredAnimation() {
			try {
				const stored = localStorage.getItem('qromo-pos-animation-preference');
				return stored && ['pig', 'elephant', 'chameleon'].includes(stored) ? stored : 'pig';
			} catch (error) {
				console.warn('Error reading animation preference from localStorage:', error);
				return 'pig';
			}
		},
		saveAnimationPreference(animationType) {
			try {
				localStorage.setItem('qromo-pos-animation-preference', animationType);
			} catch (error) {
				console.warn('Error saving animation preference to localStorage:', error);
			}
		},
		toggleAnimationSelector() {
			this.showAnimationSelector = !this.showAnimationSelector;
		},
		selectAnimation(animationType) {

			this.destroyAnimation();

			// Resetta lo stato di caricamento per forzare il reload
			const oldLoadedProperty = `loaded${this.selectedAnimation.charAt(0).toUpperCase() + this.selectedAnimation.slice(1)}`;
			this[oldLoadedProperty] = false;

			this.selectedAnimation = animationType;
			this.showAnimationSelector = false;
			this.saveAnimationPreference(animationType);
			this.loadAnimationPig(this.lastTerminalId);

			const currentAnimation = this.getCurrentAnimation();
			if(!currentAnimation) { return }

			if(this.lastCommand === "negative") {
				this.playPigNegativeLoop();
			} else if(this.lastCommand === "positive") {
				this.playPigPositiveLoop();
			} else if(this.lastCommand === "waiting") {
				this.playPigWaitingLoop();
			}
		},
		loadAnimationPig(terminalId) {
			if(typeof bodymovin === 'undefined') { return }

			if(terminalId !== undefined) {
				this.lastTerminalId = terminalId;
			}

			this.currAnimationPax = terminalId && terminalId.startsWith("pax");
			this.currAnimationDojo = terminalId && terminalId.startsWith("dojo");

			let selectedAnim = this.animations.find(anim => anim.type === this.selectedAnimation);
			let animationPath = this.currAnimationPax ? selectedAnim.urlPax : (this.currAnimationDojo ? selectedAnim.urlDojo : selectedAnim.url);
			if (!animationPath) {
				selectedAnim = this.animations.find(anim => anim.type === 'pig');
				animationPath = this.currAnimationPax ? selectedAnim.urlPax : (this.currAnimationDojo ? selectedAnim.urlDojo : selectedAnim.url);
			}
			const containerId = this.currAnimationPax ? "pig-pax-payment-" + this.uniqueId : (this.currAnimationDojo ? "pig-dojo-payment-" + this.uniqueId : "pig-payment-" + this.uniqueId);
			const loadedProperty = this.currAnimationPax ? 'loadedPigPax' : (this.currAnimationDojo ? 'loadedPigDojo' : `loaded${this.selectedAnimation.charAt(0).toUpperCase() + this.selectedAnimation.slice(1)}`);
			const animationProperty = this.currAnimationPax ? 'pigAnimationPax' : (this.currAnimationDojo ? 'pigAnimationDojo' : `${this.selectedAnimation}Animation`);

			if (!this[loadedProperty] || !this[animationProperty]) {
				this[animationProperty] = bodymovin.loadAnimation({
					container: document.getElementById(containerId),
					renderer: 'svg',
					loop: false,
					autoplay: false,
					path: animationPath,
					rendererSettings: {
						preserveAspectRatio: 'xMidYMid meet'
					}
				});

				this[loadedProperty] = true;
			}
		},
		getCurrentAnimation() {
			const animationProperty = this.currAnimationPax ? 'pigAnimationPax' : (this.currAnimationDojo ? 'pigAnimationDojo' : `${this.selectedAnimation}Animation`);
			return this[animationProperty];
		},
		pauseAnimation() {
			let currentAnimation = this.getCurrentAnimation();
			if(currentAnimation) {
				currentAnimation.pause();
			}
		},
		destroyAnimation() {
			// Distruggi tutte le possibili animazioni
			const animations = ['pig', 'elephant', 'chameleon'];
			animations.forEach(animType => {
				const normalProperty = `${animType}Animation`;
				const paxProperty = `${animType}AnimationPax`;
				const dojoProperty = `${animType}AnimationDojo`;

				if(this[normalProperty]) {
					this[normalProperty].destroy();
					this[normalProperty] = null;
				}
				if(this[paxProperty]) {
					this[paxProperty].destroy();
					this[paxProperty] = null;
				}
				if(this[dojoProperty]) {
					this[dojoProperty].destroy();
					this[dojoProperty] = null;
				}
			});
		},
		playPigWaitingLoop() {
			this.lastCommand = "waiting";
			let currentAnimation = this.getCurrentAnimation();
			if(!currentAnimation) {return}
			let onAnimReady = () => {
				currentAnimation.loop = true;
				currentAnimation.playSegments([0, 60], true);
				currentAnimation.removeEventListener('DOMLoaded', onAnimReady);
			}
			if(currentAnimation.isLoaded) {
				onAnimReady();
			} else {
				currentAnimation.addEventListener('DOMLoaded', onAnimReady);
			}
		},
		playPigPositiveLoop() {
			let wasWaiting = this.lastCommand === "waiting";
			this.lastCommand = "positive";
			let currentAnimation = this.getCurrentAnimation();
			if(!currentAnimation) {return}
			const frameHandler = () => {
				if(currentAnimation.currentFrame >= 298) {
					if(this.lastCommand === "positive") {
						currentAnimation.loop = true;
						currentAnimation.playSegments([300, 360], true);
					}
					currentAnimation.removeEventListener('enterFrame', frameHandler);
				}
			};
			let onAnimReady = () => {
				currentAnimation.addEventListener('enterFrame', frameHandler);
				currentAnimation.playSegments([60, 360], wasWaiting === false);
			}
			if(currentAnimation.isLoaded) {
				onAnimReady();
			} else {
				currentAnimation.addEventListener('DOMLoaded', onAnimReady);
			}
		},
		playPigNegativeLoop() {
			this.lastCommand = "negative";
			let currentAnimation = this.getCurrentAnimation();
			if(!currentAnimation) {return}
			const frameHandler = () => {
				if(currentAnimation.currentFrame >= 178) {
					if(this.lastCommand === "negative") {
						currentAnimation.loop = true;
						currentAnimation.playSegments([480, 540], true);
					}
					currentAnimation.removeEventListener('enterFrame', frameHandler);
				}
			};
			let onAnimReady = () => {
				currentAnimation.addEventListener('enterFrame', frameHandler);
				currentAnimation.playSegments([360, 540], true);
			}
			if(currentAnimation.isLoaded) {
				onAnimReady();
			} else {
				currentAnimation.addEventListener('DOMLoaded', onAnimReady);
			}
		},
		isAnimationPaused() {
			let currentAnimation = this.getCurrentAnimation();
			return currentAnimation && currentAnimation.isPaused;
		},
	},
	created() {
		this.uniqueId = Math.random().toString(36).substring(2, 15);
	},
	beforeUnmount() {
		this.destroyAnimation();
	}
}
</script>

<style scoped lang="scss">
.animation-container {
	position: relative;

	&:not(.no-pointer) {
		cursor: pointer;
	}
}

.animation-selector {
	position: absolute;
	bottom: 15px;
	left: 50%;
	transform: translateX(-50%);
	display: flex;
	gap: 30px;
	background: var(--color-bg-primary-1);
	border-radius: 13px;
	padding: 6px;
	box-shadow: 0 4px 12px var(--color-box-shadow-2);
	animation: fadeIn 0.3s ease-in-out;
	z-index: 10;
}

@keyframes fadeIn {
	from {
		opacity: 0;
		transform: translateX(-50%) translateY(10px);
	}
	to {
		opacity: 1;
		transform: translateX(-50%) translateY(0);
	}
}

.animation-icon {
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 8px;
	cursor: pointer;
	transition: all 0.2s ease;
	border: 2px solid transparent;

	:deep(svg) {
		width: 24px;
		height: 24px;

		.fill {
			fill: var(--color-primary);
			transition: fill 0.2s ease;
		}
	}

	&:hover {
		background: var(--color-border-1);
	}

	&.active {
		background: var(--color-border-2);

		:deep(svg .fill) {
			fill: var(--color-danger);
		}
	}
}

.pig-animation {
	min-height: 200px;
	display: flex;
	align-items: center;
	justify-content: center;
}
</style>